import * as React from 'react'
import { Button, ButtonComponent, Checkbox, FilterPanel, IconButton, ICallback, Input, Label, MultiSelect, PortalContainer } from 'uxp/components'
import { IContextProvider } from '../../../uxp'
import { addToPath, findAvailableFields, IPropertyEditorProps, ISerializedUIField, IUIPropertyDefinitionMap, IWDDesignModeProps } from '../common'
import { FieldContainer } from '../FieldContainer'
import { $L } from '../../../localize'
import { Transition } from 'uxp/components';
import classNames from 'classnames'
import { SelectableItem, selectUIProps } from '../SelectableItem'
import { BooleanProperty } from '../ui-props/BooleanProperty'
import { Property, PropertyCard, PropertyContainer, PropertyGroup, PropertyGroupTitle, PropertyTitle } from '../ui-props/common'
import { IDataSourceBinding, executeAndBind } from '../data-source-picker/datasource'
import { DataSourcePropertyEditor } from '../ui-props/DataSourceProperty'
import jp from 'jsonpath'
import { generateFullUUID } from '../../../util'
import { useExportData } from 'uxp/components'
import { format } from 'date-fns'
import './WDFilterPanel.scss'

interface IWDFilterPanelProps {
    designer: IWDDesignModeProps,
    uxpContext: IContextProvider,
    uiProps?: any,
    containerRef: React.MutableRefObject<any>
    data: { [key: string]: any }
    onChangeFilters: (data: any) => void,
    onClearFilters: () => void,
    onOpenPanel?: () => void,
    onClosePanel?: () => void
    exportFileName?: string
}


interface IWDFilterPanelProperties {
    fillContainer: boolean,
    enableClear: boolean,
    enableExport: boolean
}

interface IExportSheet {
    id: string
    name: string,
    binding: IDataSourceBinding,
    excludeFields?: boolean,
    fieldsToInclude?: string[],
    fieldsToExclude?: string[]
}

/**
 * Whether a widget should render its filter panel. In preview/thumbnail it's
 * always shown (so it can be configured/seen); otherwise show it when there are
 * filter fields OR when export is enabled and has at least one sheet configured
 * (a widget with export-only still needs the panel to reach the Export button).
 */
export function shouldRenderFilterPanel(uiProps: any, mode?: string): boolean {
    if (mode === 'preview' || mode === 'thumbnail') return true;
    const hasFields = Number(uiProps?.filter?.fields?.length) > 0;
    const hasExport = uiProps?.filter?.enableExport !== false && Number(uiProps?.export?.sheets?.length) > 0;
    return hasFields || hasExport;
}

const PropertyEditor: React.FunctionComponent<IPropertyEditorProps<IWDFilterPanelProperties>> = (props) => {

    return <PropertyContainer>
        <PropertyGroup>
            <PropertyGroupTitle>{$L('widgetdesigner.props.filter-panel', 'Filter Panel')}</PropertyGroupTitle>

            <Property>
                <Checkbox
                    type='bordered'
                    checked={props.uiProps.fillContainer || false}
                    onChange={s => props.onChange(Object.assign({}, props.uiProps, { fillContainer: s }))}
                    className="widget-designer-input"
                    label={$L('widgetdesigner.props.fill-widget', 'Expand To Fill the widget')}
                />
                <Checkbox
                    type='bordered'
                    checked={props.uiProps.enableClear || false}
                    onChange={s => props.onChange(Object.assign({}, props.uiProps, { enableClear: s }))}
                    className="widget-designer-input"
                    label={$L('widgetdesigner.props.enable-clear', 'Enable Clear button')}
                />
                <Checkbox
                    type='bordered'
                    checked={props.uiProps.enableExport || false}
                    onChange={s => props.onChange(Object.assign({}, props.uiProps, { enableExport: s }))}
                    className="widget-designer-input"
                    label={$L('widgetdesigner.props.enable-export', 'Enable Export button')}
                />
            </Property>

        </PropertyGroup>
    </PropertyContainer>;
}

const ExportPropertyEditor: React.FunctionComponent<IPropertyEditorProps<{ sheets: IExportSheet[] }>> = (props) => {

    const _props = props.uiProps || { sheets: [] }

    function addSheet() {
        const _sheets = [..._props.sheets]
        _sheets.push({ id: generateFullUUID(), name: `Sheet ${_sheets.length + 1}`, binding: null, excludeFields: false, fieldsToExclude: [], fieldsToInclude: [] })
        props.onChange(Object.assign({}, _props, { sheets: _sheets }))
    }

    return <PropertyContainer>
        <PropertyGroup>
            <PropertyGroupTitle>{$L('widgetdesigner.props.export-data', 'Export Data')}</PropertyGroupTitle>

            <Property>
                <PropertyTitle>{$L('widgetdesigner.props.configure-export', 'Configure Data to export')}</PropertyTitle>

                <div className="uxp_wd-fp__sheets">
                    {
                        _props?.sheets?.map((s: IExportSheet, i) => {

                            let possibleValues = {};
                            findAvailableFields(s.binding, props?.parentUIProps?.datasources || [], possibleValues);
                            let pv = Object.keys(possibleValues);
                            pv.sort();

                            return <PropertyCard
                                key={s.id}
                                onRemove={() => {
                                    const filtered = _props.sheets.filter(sheet => sheet.id !== s.id)
                                    props.onChange(Object.assign({}, _props, { sheets: filtered }))
                                }}
                            >

                                <Label> {$L('widgetdesigner.props.sheet-name', 'Sheet Name')} </Label>
                                <Input value={s.name} onChange={(s) => {
                                    jp.apply(_props, `$.sheets[${i}].name`, () => s)
                                    props.onChange(_props)
                                }} />

                                <DataSourcePropertyEditor
                                    binding={s.binding}
                                    context={props.uxpContext}
                                    onChange={(binding) => {
                                        jp.apply(_props, `$.sheets[${i}].binding`, () => binding)
                                        props.onChange(_props)
                                    }}
                                    onChangeAny={(p, v) => {
                                        props.onChangeAny(p, v);
                                    }}
                                    sources={props?.parentUIProps?.datasources || []}
                                    target={'dictionary-array'}
                                    title={$L('widgetdesigner.props.data-source', 'Data Source')}
                                    contextData={props.contextData}
                                    className='uxp-designer-data-source-selector'
                                    designer={props.designer}
                                />


                                <Checkbox
                                    checked={s?.excludeFields || false}
                                    onChange={(v) => {

                                        const _sheet = Object.assign({}, { ..._props.sheets[i], excludeFields: v })
                                        jp.apply(_props, `$.sheets[${i}]`, () => _sheet)
                                        props.onChange(_props)
                                    }}
                                    label={$L('widgetdesigner.props.exclude-field-export', 'Exclude Field from export')}
                                />

                                {s?.excludeFields ? <>
                                    <Label>{$L('widgetdesigner.props.exclude-fields', 'Exclude fields')}</Label>
                                    <small>{$L('widgetdesigner.props.export-all-hint', 'If empty all the fields will be exported')}</small>
                                    <MultiSelect
                                        selected={s.fieldsToExclude || []}
                                        onChange={v => {

                                            const _sheet = Object.assign({}, { ..._props.sheets[i], fieldsToExclude: v })
                                            jp.apply(_props, `$.sheets[${i}]`, () => _sheet)
                                            props.onChange(_props)
                                        }}
                                        options={pv.map(v => ({ label: v, value: v }))}
                                        className="widget-designer-input"
                                        showEndOfContent={false}
                                    />
                                </> : <>

                                    <Label>{$L('widgetdesigner.props.include-fields', 'Include fields')}</Label>
                                    <small>{$L('widgetdesigner.props.export-all-hint', 'If empty all the fields will be exported')}</small>
                                    <MultiSelect
                                        selected={s.fieldsToInclude || []}
                                        onChange={v => {

                                            const _sheet = Object.assign({}, { ..._props.sheets[i], fieldsToInclude: v })
                                            jp.apply(_props, `$.sheets[${i}]`, () => _sheet)
                                            props.onChange(_props)
                                        }}
                                        options={pv.map(v => ({ label: v, value: v }))}
                                        className="widget-designer-input"
                                        showEndOfContent={false}
                                    />
                                </>
                                }
                            </PropertyCard>
                        })
                    }
                </div>

                <Button
                    className="new-option"
                    title={$L('widgetdesigner.props.add-sheet', 'Add Sheet')}
                    icon={"/Resources/Lucy/lucy-dashboard/assets/images/plus-rounded.svg"}
                    iconPosition="right"
                    onClick={addSheet}
                />

            </Property>

        </PropertyGroup>
    </PropertyContainer>
}

const WDFilterPanel: React.FunctionComponent<IWDFilterPanelProps> = (props) => {

    let [parentStyles, setParentStyles] = React.useState<any>({})
    let [childStyles, setChildStyles] = React.useState<any>({})
    let [show, setShow] = React.useState(false)
    // let [data, setData] = React.useState({})

    const buttonRef = React.useRef(null)
    const filterPanelContainerRef = React.useRef(null)

    let filterProps = props?.uiProps?.filter || { fillContainer: false, enableClear: true, enableExport: true, fields: [] }
    let exportProps = props?.uiProps?.export || { sheets: [] }

    // "Rendered" = not the editable design canvas (mirrors the droppable's live
    // check in dnd.tsx). Used to gate fill-to-container so design mode keeps the
    // filter field droppable configurable.
    const isRenderedMode = !props?.designer || props.designer.mode == 'thumbnail' || props.designer.mode == 'live'

    const { exportData: runExport } = useExportData();


    React.useEffect(() => {
        document.addEventListener("click", (e) => handleClick(e))

        return () => {
            document.removeEventListener("click", (e) => handleClick(e))
        }
    }, [])

    React.useEffect(() => {
        handleButtonClick()
    }, [filterProps.fillContainer])

    React.useEffect(() => {
        if (show) {
            if (props.onOpenPanel) props.onOpenPanel()
        }
        else {
            if (props.onClosePanel) props.onClosePanel()
        }
    }, [show])

    function closePanel() {
        setShow(false)
    }

    function clearFilters() {
        props.onClearFilters()
    }

    // on click modal or backdrop 
    const handleClick = (e: MouseEvent) => {
        if (e.target == filterPanelContainerRef.current) {
            closePanel()
        }
    }

    function handleButtonClick() {

        const buttonDetails = buttonRef.current.getBoundingClientRect();
        const containerDetails = props.containerRef.current.getBoundingClientRect();

        let parentStyles: any = {}
        let childStyles: any = {
            top: buttonDetails?.y + window.scrollY - 10,
            right: window.innerWidth - buttonDetails.right - 10,
        }

        if (props?.designer?.mode == 'preview') {
            parentStyles = {
                top: containerDetails.top + window.scrollY,
                left: containerDetails.left,
                width: containerDetails.width,
                height: containerDetails.height,
            }

            childStyles = {
                top: 0,
                right: 0
            }

            // NOTE: this `mode=='preview'` branch actually runs in the editable
            // DESIGN canvas — templates always get designer.mode=='preview' inside
            // the editor (the real preview session passes designer=null and flows
            // through the `else if` below). "Expand to fill" fills the widget here
            // too; the .uxp_wd-fp--fill modifier (WDFilterPanel.scss) overrides the
            // uxpcore_filterpanel max-width/max-height caps and lets the body shrink
            // so the drop-area stays visible/scrollable and configurable.
            if (filterProps.fillContainer) {
                childStyles = {
                    top: 0,
                    left: 0,
                    width: '100%',
                    // fill the widget, but grow (never clip) when the drop-area floor
                    // + footer + export need more room than the widget's height
                    minHeight: '100%'
                }
            }
        }
        // Fill-to-container only when the widget is RENDERED (thumbnail/live/no
        // designer) — mirrors the droppable's own live-mode check (dnd.tsx). In the
        // editable design canvas the panel must stay a normal, configurable size so
        // the droppable field area is usable.
        else if (filterProps.fillContainer && isRenderedMode) {
            childStyles = {
                top: containerDetails.top + window.scrollY,
                left: containerDetails.left,
                width: containerDetails.width,
                height: containerDetails.height
            }
        }

        setParentStyles(parentStyles);
        setChildStyles(childStyles);
    }

    function processData(data: any[], sheet: IExportSheet) {

        if (sheet?.excludeFields) {
            if (sheet?.fieldsToExclude?.length == 0) return data
            const updated = data.map(d => {
                const _row = { ...d }
                sheet.fieldsToExclude.forEach(f => delete _row[f])
                return _row
            })

            return updated
        }

        if (sheet?.fieldsToInclude?.length > 0) {
            const updated = data.map(d => {
                const _row: any = {}
                sheet.fieldsToInclude.forEach(f => { _row[f] = d[f] })
                return _row
            })

            return updated
        }

        return data
    }

    async function prepareData(sheets: IExportSheet[]) {
        const exportData: { [sheet: string]: any[] } = {}

        for (const sheet of sheets) {
            try {
                const data = await executeAndBind(props.uxpContext, sheet.binding, props?.uiProps?.datasources || [], {}, props.data)
                if (data) {
                    exportData[sheet.name] = processData(data, sheet)
                }
            } catch (e) {
                console.log('unable to get the data to export ', sheet, e)
            }
        }
        return exportData
    }
    async function exportData() {
        if (!exportProps?.sheets || exportProps?.sheets?.length == 0) return
        const prepared = await prepareData(exportProps.sheets)

        // Multi-sheet Excel via uxp useExportData (format:'xlsx'). Each configured
        // sheet becomes a workbook tab; columns are derived from the row keys.
        const sheets = Object.entries(prepared)
            .filter(([, rows]) => Array.isArray(rows) && rows.length > 0)
            .map(([name, rows]) => ({
                name,
                data: rows,
                total: rows.length,
                columns: Object.keys(rows[0] || {}).map(k => ({ id: k, label: k })),
            }))
        if (sheets.length === 0) return

        const fileName = props.exportFileName || `${props?.uiProps?.__widget_name__ || 'Data'}-${format((new Date()), 'yyyy-MM-dd-HH-mm')}`

        await runExport({ sheets, format: 'xlsx', fileName })
    }

    function renderContent() {
        return <div className={classNames("uxp_wd-fp", {'wd-design-mode': props?.designer?.mode })} ref={filterPanelContainerRef} style={parentStyles}>
            <div className={classNames("uxp-filter-panel", "uxpcore_filterpanel", "uxpcore_filterpanel__content", { 'uxp_wd-fp--fill': filterProps.fillContainer })}
                style={childStyles}
            >
                <div className="uxp-filter-header uxpcore_filterpanel__header">

                    <div className="title uxpcore_filterpanel__title">
                        <IconButton type="filter" variant="secondary" size="small" disabled />
                        <span className="text uxpcore_filterpanel__title-text">Filters</span>
                    </div>

                    <IconButton type="close" variant="secondary" size="small" onClick={closePanel} />

                </div>

                <div className="uxp-filter-body uxpcore_filterpanel__body" >
                    <FieldContainer
                        designer={props.designer}
                        uxpContext={props.uxpContext}
                        contextData={props.data}
                        dataKey="filters"
                        onChangeData={data => {

                            props.onChangeFilters(data || {})
                        }}
                        uiProps={props.uiProps}
                        propPath={['filter', 'fields']}
                        onChangeFields={(fields) => {
                            let _uiProps = Object.assign({}, props.uiProps)
                            if (!_uiProps.filter) _uiProps.filter = Object.assign({}, filterProps)
                            _uiProps.filter.fields = fields
                            props?.designer?.setUIProps(_uiProps)
                        }}
                    />
                </div>

                <div className={classNames("uxp-filter-footer", "uxpcore_filterpanel__footer")}>
                    <ButtonComponent
                        title="Clear"
                        variant="secondary"
                        size="small"
                        disabled={!filterProps?.enableClear}
                        onClick={clearFilters}
                    />
                </div>

                {filterProps?.enableExport &&
                    <SelectableItem
                        designer={props.designer}
                        props={[
                            {
                                id: addToPath('export'),
                                value: exportProps,
                                definition: {
                                    defaults: { sheets: [] },
                                    render: ExportPropertyEditor
                                }
                            }
                        ]}
                    >
                        <div className="uxp_wd-fp__export">
                            <Label>Export data</Label>

                            <ButtonComponent
                                title='Export'
                                variant="primary"
                                size="small"
                                onClick={() => {
                                    if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) return
                                    exportData()
                                }}
                            />
                        </div>
                    </SelectableItem>
                }
            </div>
        </div>
    }

    return <>

        <SelectableItem
            designer={props.designer}
            props={[
                {
                    id: addToPath('filter'),
                    value: filterProps,
                    definition: {
                        defaults: { fillContainer: false, enableClear: true, enableExport: true },
                        render: PropertyEditor
                    }
                }
            ]}
        >

            <span className="wd-filter-panel-btn" ref={buttonRef}>
                <IconButton
                    type="filter"
                    variant="secondary"
                    size="medium"
                    onClick={() => {
                        handleButtonClick()
                        setShow(true)
                    }}
                />
            </span>
        </SelectableItem>

        <Transition
            in={show}
            duration={201.25}
            className={"uxp_wd-fp-fade"}
            unmountOnExit={true}
        >
            <PortalContainer>
                {renderContent()}
            </PortalContainer>
        </Transition>
    </>
}

export default WDFilterPanel